import { Box, CircularProgress, IconButton, Tooltip, Typography } from "@mui/material"
import {
  AddOutlined,
  DeleteOutlined,
  KeyboardArrowDownOutlined,
  KeyboardArrowUpOutlined,
} from "@mui-symbols-material/w300"
import type { FC } from "react"
import { useState } from "react"
import { FormProvider, useWatch } from "react-hook-form"
import { useShallow } from "zustand/shallow"

import { CustomerPurchaseOrderLineItemCard } from "./CustomerPurchaseOrderLineItemCard"
import { CustomerPurchaseOrderNoProductsBanner } from "./CustomerPurchaseOrderNoProductsBanner"
import { CustomerPurchaseOrderStatusChip } from "./CustomerPurchaseOrderStatusChip"

import { Button } from "@/components/common/Button"
import { CountChip } from "@/components/common/CountChip"
import CancelDialog from "@/components/common/Modals/CancelDialog"
import { UOM_RENDER_PRECISION } from "@/constants/formats"
import type { CustomerPurchaseOrderListItem } from "@/graphql/codegen/graphql"
import { CustomerPurchaseOrderStatus } from "@/graphql/codegen/graphql"
import { ProductSelectionDialog } from "@/screens/CustomerOrders/components/ProductSelectionDialog/ProductSelectionDialog"
import { useCalculateCustomerPurchaseOrderPricingTotals } from "@/screens/CustomerOrders/hooks/useCalculateCustomerPurchaseOrderPricingTotals"
import { useCustomerPurchaseOrderLineItemsForm } from "@/screens/CustomerOrders/hooks/useCustomerPurchaseOrderLineItemsForm"
import { useGetDeliveryDateLineDefaults } from "@/screens/CustomerOrders/hooks/useGetDeliveryDateLineDefaults"
import type { SelectedProduct } from "@/screens/CustomerOrders/types"
import { formatShippedDate, getCpoHeaderTotals } from "@/screens/CustomerOrders/utils"
import { useUnitOfMeasurementsStore } from "@/stores/unitOfMeasurmentsStore"
import { formatDate } from "@/utils/date"
import { formatNumber } from "@/utils/number"

interface CustomerPurchaseOrderRowProps {
  cpo: CustomerPurchaseOrderListItem
  companyId: string
  shipToLocationId: string
  canEdit: boolean
  canAddProducts: boolean
  onDelete: (cpo: CustomerPurchaseOrderListItem) => void
  onDirtyChange: (cpoId: string, isDirty: boolean) => void
}

export const CustomerPurchaseOrderRow: FC<CustomerPurchaseOrderRowProps> = ({
  cpo,
  companyId,
  shipToLocationId,
  canEdit,
  canAddProducts,
  onDelete,
  onDirtyChange,
}) => {
  const [isProductSelectionOpen, setIsProductSelectionOpen] = useState(false)
  const [isExpanded, setIsExpanded] = useState(false)

  const { preferredShipFromLocationId, preferredMeasurementSystem } = useGetDeliveryDateLineDefaults(
    companyId,
    shipToLocationId
  )

  const {
    methods,
    lineItemFields,
    isLoadingLineItems,
    appendLineItems,
    removeLineItem,
    handleSave,
    isSaving,
    handleDiscard,
    isDiscardConfirmationOpen,
    setIsDiscardConfirmationOpen,
  } = useCustomerPurchaseOrderLineItemsForm({
    customerPurchaseOrderId: cpo.customerPurchaseOrderId,
    isExpanded,
    preferredShipFromLocationId,
    preferredMeasurementSystem,
    onDirtyChange,
  })

  const {
    control,
    formState: { isDirty, isValid, isSubmitting, isSubmitted },
  } = methods

  const isCpoCancelled = cpo.status === CustomerPurchaseOrderStatus.Cancelled

  const lineItems = useWatch({ control, name: "lineItems" })
  const { totals, isFetching: isPricingFetching } = useCalculateCustomerPurchaseOrderPricingTotals(companyId, lineItems)

  const units = useUnitOfMeasurementsStore(useShallow((state) => state.units))
  const hasLoadedLineItems = isExpanded && !isLoadingLineItems
  const {
    pallets,
    units: unitsCount,
    quantityInUom,
    uomId,
    shippingDate,
  } = getCpoHeaderTotals({
    cpo,
    liveTotal: hasLoadedLineItems ? totals?.total : undefined,
    lineItems,
    hasNoLineItems: hasLoadedLineItems && lineItems.length === 0,
  })
  const uomLabel = units.find((unit) => unit.id === uomId)?.name
  const hasQuantity = Boolean(quantityInUom)

  // Figma: Save Changes stays clickable even with incomplete required fields — clicking it runs
  // validation and reveals exactly what's wrong (red field borders via each FormControl's own
  // fieldState.error, plus this banner) rather than leaving the user with an inert disabled
  // button and no indication of what to fix.
  const showValidationError = isSubmitted && !isValid

  const handleProductsAdded = (products: SelectedProduct[]): boolean => {
    // appendLineItems is a local RHF field-array append and cannot fail — the product isn't
    // persisted until the user clicks Save Changes, which runs the real upsertProducts mutation.
    appendLineItems(products)
    setIsProductSelectionOpen(false)
    return true
  }

  const handleDiscardConfirm = () => {
    methods.reset()
    setIsDiscardConfirmationOpen(false)
  }

  const handleProductSelectionOpen = () => {
    // Line items only load once expanded — expand first so the CPO's existing lines are loaded
    // before the user can append new ones, otherwise appendLineItems would be wiped out by the
    // load-on-expand reset.
    setIsExpanded(true)
    setIsProductSelectionOpen(true)
  }

  const handleToggleExpand = () => setIsExpanded((prev) => !prev)

  return (
    <Box className='rounded-md border border-solid border-gray-400'>
      <Box className='flex flex-col gap-1 rounded-md bg-primary-100 p-6'>
        <Box className='flex flex-nowrap items-center justify-between gap-4'>
          <Box className='flex min-w-0 flex-1 items-center gap-2'>
            <Tooltip title={`CPO#: ${cpo.cpoNumber}`}>
              <Typography variant='body1' className='min-w-0 truncate text-lg text-gray-800'>
                CPO#: {cpo.cpoNumber}
              </Typography>
            </Tooltip>
            <CustomerPurchaseOrderStatusChip status={cpo.status} />
            <Typography variant='body2' className='shrink-0 whitespace-nowrap text-blue-gray-600'>
              Created {formatDate(cpo.createdOn)}
            </Typography>
          </Box>

          <Box className='flex shrink-0 flex-nowrap items-center justify-end gap-4'>
            <CountChip
              label='Pallets'
              value={formatNumber(pallets, UOM_RENDER_PRECISION, 0)}
              className='min-w-[100px]'
              data-testid={`customer-orders.cpo-row.pallets-count-${cpo.customerPurchaseOrderId}`}
            />
            <CountChip
              label='Units'
              value={formatNumber(unitsCount, UOM_RENDER_PRECISION, 0)}
              className='min-w-[100px]'
              data-testid={`customer-orders.cpo-row.units-count-${cpo.customerPurchaseOrderId}`}
            />
            <CountChip
              label='UoM'
              value={
                hasQuantity
                  ? `${formatNumber(quantityInUom, UOM_RENDER_PRECISION, 0)} ${uomLabel ?? "(Mixed Units)"}`
                  : "—"
              }
              className='min-w-[100px]'
              data-testid={`customer-orders.cpo-row.uom-count-${cpo.customerPurchaseOrderId}`}
            />
            <CountChip
              label='Shipped Date'
              value={formatShippedDate(shippingDate)}
              className='min-w-[100px]'
              data-testid={`customer-orders.cpo-row.shipped-date-${cpo.customerPurchaseOrderId}`}
            />
            <Button
              disabled={!canEdit || !isDirty || isSubmitting}
              loading={isSaving}
              onClick={handleSave}
              data-testid={`customer-orders.cpo-row.save-changes-button-${cpo.customerPurchaseOrderId}`}
            >
              Save Changes
            </Button>
            <Button
              appearance='outlined'
              disabled={!canEdit || !isDirty}
              onClick={handleDiscard}
              data-testid={`customer-orders.cpo-row.discard-changes-button-${cpo.customerPurchaseOrderId}`}
            >
              Discard Changes
            </Button>
            <Button
              appearance='outlined'
              startIcon={<AddOutlined />}
              disabled={!canAddProducts}
              className='!border-primary-300 !text-primary'
              onClick={handleProductSelectionOpen}
              data-testid={`customer-orders.cpo-row.add-product-button-${cpo.customerPurchaseOrderId}`}
            >
              Product
            </Button>
            <IconButton
              aria-label='delete CPO'
              disabled={!canEdit || isCpoCancelled}
              onClick={() => onDelete(cpo)}
              data-testid={`customer-orders.cpo-row.delete-button-${cpo.customerPurchaseOrderId}`}
            >
              <DeleteOutlined />
            </IconButton>
            <IconButton
              aria-label={isExpanded ? "collapse CPO" : "expand CPO"}
              onClick={handleToggleExpand}
              data-testid={`customer-orders.cpo-row.collapse-button-${cpo.customerPurchaseOrderId}`}
            >
              {isExpanded ? <KeyboardArrowUpOutlined /> : <KeyboardArrowDownOutlined />}
            </IconButton>
          </Box>
        </Box>

        {showValidationError && (
          <Typography
            variant='body2'
            className='mt-4 text-right text-error-400'
            data-testid={`customer-orders.cpo-row.validation-error-${cpo.customerPurchaseOrderId}`}
          >
            Some products have incomplete required information. Review the highlighted fields and try again.
          </Typography>
        )}
      </Box>

      {isExpanded && (
        <FormProvider {...methods}>
          <Box className='flex flex-col gap-4 p-6'>
            {isLoadingLineItems && (
              <Box className='flex items-center justify-center p-8'>
                <CircularProgress
                  data-testid={`customer-orders.cpo-row.line-items-loading-${cpo.customerPurchaseOrderId}`}
                />
              </Box>
            )}
            {!isLoadingLineItems &&
              lineItemFields.length > 0 &&
              lineItemFields.map((field, lineItemIndex) => (
                <CustomerPurchaseOrderLineItemCard
                  key={field.id}
                  lineItemIndex={lineItemIndex}
                  shipToLocationId={shipToLocationId}
                  preferredShipFromLocationId={preferredShipFromLocationId}
                  preferredMeasurementSystem={preferredMeasurementSystem}
                  productResult={totals?.products[lineItemIndex]}
                  isPricingFetching={isPricingFetching}
                  isCpoCancelled={isCpoCancelled}
                  onDelete={() => removeLineItem(lineItemIndex)}
                />
              ))}
            {!isLoadingLineItems && lineItemFields.length === 0 && !isCpoCancelled && (
              <CustomerPurchaseOrderNoProductsBanner />
            )}
          </Box>
        </FormProvider>
      )}

      {isProductSelectionOpen && (
        <ProductSelectionDialog
          companyId={companyId}
          shipToLocationId={shipToLocationId}
          cpoProductIds={lineItemFields.map((field) => field.productId)}
          onClose={() => setIsProductSelectionOpen(false)}
          onProductsAdded={handleProductsAdded}
        />
      )}

      {isDiscardConfirmationOpen && (
        <CancelDialog
          title='Discard changes?'
          message='Discarding now will remove all unsaved changes to this customer purchase order.'
          closeLabel='Go Back'
          confirmLabel='Discard Changes'
          onClose={() => setIsDiscardConfirmationOpen(false)}
          onConfirm={handleDiscardConfirm}
        />
      )}
    </Box>
  )
}
